<template>
  <div class="document-container">
    <!-- 搜索与操作栏 -->
    <div class="action-bar">
      <div class="action-group">
        <el-input
            v-model="send.name"
            placeholder="请输入文件名称"
            clearable
            class="search-input"
            prefix-icon="el-icon-search"
        ></el-input>
        <el-button type="primary" @click="selectListPage" icon="el-icon-search">搜索</el-button>
        <el-button @click="reset" icon="el-icon-refresh-right">重置</el-button>
      </div>
      <div class="action-group">
        <el-upload
            :action="`https://${WHITE_IP}/document/upload`"
            name="multipartFile"
            :show-file-list="false"
            accept=".xlsx,.xls"
            :on-success="onImportSuccess"
            :before-upload="beforeUpload"
        >
          <el-button type="success" icon="el-icon-upload2">上传文件</el-button>
        </el-upload>
        <el-button
            type="primary"
            icon="el-icon-download"
            @click="batchDownload"
            :disabled="!sendForm.removeIdList || sendForm.removeIdList.length === 0"
        >
          批量下载
        </el-button>
        <el-popconfirm
            confirm-button-text="确定"
            cancel-button-text="取消"
            icon="el-icon-warning"
            icon-color="#F56C6C"
            title="确认要批量删除选中的文件吗？"
            @confirm="confirmRemoveIdList"
        >
          <el-button type="danger" icon="el-icon-delete" slot="reference"
                     :disabled="!sendForm.removeIdList || sendForm.removeIdList.length === 0">
            批量删除
          </el-button>
        </el-popconfirm>
      </div>
    </div>

    <!-- 数据表格 -->
    <el-table
        :data="list"
        v-loading="loading"
        stripe
        border
        style="width: 100%"
        @selection-change="onSelectChange"
        :header-cell-style="headerCellStyle"
        class="data-table"
    >
      <el-table-column type="selection" width="50" fixed="left"></el-table-column>
      <el-table-column type="index" :index="hIndex" label="序号" width="60" align="center"></el-table-column>
      <el-table-column prop="name" label="文件名称" width="150" show-overflow-tooltip></el-table-column>
      <el-table-column prop="type" label="文件类型" width="100" align="center">
        <template slot-scope="scope">
          <el-tag size="small" effect="plain">{{ scope.row.type || '-' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="size" label="文件大小" width="110" align="right">
        <template slot-scope="scope">
          {{ formatFileSize(scope.row.size) }}
        </template>
      </el-table-column>
      <el-table-column prop="uploadTime" label="上传时间" width="170" align="center">
        <template slot-scope="scope">
          {{ scope.row.uploadTime || '-' }}
        </template>
      </el-table-column>
      <!-- 转写内容列 - 使用el-tooltip实现悬停显示完整内容 -->
      <el-table-column prop="displayContent" label="转写内容" min-width="180">
        <template slot-scope="scope">
          <el-tooltip
              :content="scope.row.displayContent || '暂无'"
              placement="top-start"
              :disabled="!scope.row.displayContent || scope.row.displayContent.length === 0"
              :open-delay="300"
          >
            <div class="content-preview-wrapper">
              <span class="content-preview">{{ scope.row.displayContent || '暂无' }}</span>
            </div>
          </el-tooltip>
        </template>
      </el-table-column>
      <!-- 统计列 - 基于转写内容本地计算（径级分段/根数/方量），直接展示 -->
      <el-table-column prop="statText" label="统计" min-width="200">
        <template slot-scope="scope">
          <div class="stat-cell">
            <div class="stat-wood-len">
              <span class="stat-label">材长:</span>
              <el-input-number v-model="scope.row.woodLen" :min="0.1" :step="0.1" :precision="2" size="mini"
                               style="width: 110px" @change="onWoodLenChange(scope.row)"></el-input-number>
            </div>
            <pre v-if="scope.row.statText" class="stat-pre">{{ scope.row.statText }}</pre>
            <span v-else class="stat-empty">无统计</span>
          </div>
        </template>
      </el-table-column>
      <!-- 音频文件地址与播放 -->
      <el-table-column label="音频播放" width="200" align="center">
        <template slot-scope="scope">
          <div class="file-address-cell">
            <!-- 每行常驻播放条，由按钮/原生控件触发播放，支持多次点击 -->
            <div v-if="isWavFile(scope.row.url)" class="inline-audio-player">
              <audio
                  :ref="'audio_' + scope.row.id"
                  :src="getFullUrl(scope.row.url)"
                  controls
                  class="inline-audio"
                  controlsList="nodownload"
                  @play="onAudioPlay(scope.row)"
                  @pause="onAudioPause(scope.row)"
                  @ended="onAudioEnded(scope.row)"
              ></audio>
            </div>
            <el-button
                v-if="isWavFile(scope.row.url)"
                type="text"
                :icon="currentPlayingId === scope.row.id ? 'el-icon-refresh' : 'el-icon-headset'"
                @click="playInlineAudio(scope.row)"
                class="play-inline-btn"
                :class="{ 'is-playing': currentPlayingId === scope.row.id }"
            >{{ currentPlayingId === scope.row.id ? '重新播放' : '播放音频' }}
            </el-button>
            <span v-if="!isWavFile(scope.row.url)" class="empty-text">非WAV文件</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="下载" width="70" align="center">
        <template slot-scope="scope">
          <el-button type="primary" icon="el-icon-download" size="small" circle
                     @click="list_export(scope.row)"></el-button>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="80" align="center">
        <template slot-scope="scope">
          <el-switch
              v-model="scope.row.enable"
              active-color="#13ce66"
              inactive-color="#dcdfe6"
              @change="changeEnable(scope.row)"
          ></el-switch>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="70" fixed="right" align="center">
        <template #default="scope">
          <el-popconfirm
              confirm-button-text="删除"
              cancel-button-text="取消"
              icon="el-icon-warning"
              icon-color="#F56C6C"
              title="确认删除该文件吗？"
              @confirm="confirmDelete(scope.row.id)"
          >
            <el-button type="danger" icon="el-icon-delete" size="small" circle slot="reference"></el-button>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页组件 -->
    <div class="pagination-wrapper">
      <el-pagination
          @size-change="onSizeChange"
          @current-change="onCurrentChange"
          :current-page="send.currentPage"
          :page-sizes="[10, 15, 20, 50]"
          :page-size="send.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="send.total"
          background
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import {WHITE_IP} from "../../public/config";
import router from "@/js_router/router";
import JSZip from 'jszip';
import {saveAs} from 'file-saver';
import {calcStat} from "@/js_util/statUtil";

export default {
  name: "Document",
  data() {
    return {
      WHITE_IP: WHITE_IP,
      loading: false,
      list: [],
      send: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
        name: ''
      },
      sendForm: {
        removeIdList: []
      },
      currentPlayingId: null, // 当前正在播放的音频行ID
      audioCache: {}, // 音频URL -> Blob URL缓存，避免重复下载
      headerCellStyle: {
        'background-color': '#f5f7fa',
        'color': '#303133',
        'font-weight': '500',
        'border-bottom': '1px solid #e4e7ed'
      }
    }
  },
  created() {
    this.selectListPage()
  },
  methods: {
    hIndex(index) {
      return (this.send.currentPage - 1) * this.send.pageSize + index + 1
    },
    // 格式化文件大小
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '-'
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    // 判断是否为wav文件
    isWavFile(url) {
      if (!url) return false
      const lowerUrl = url.toLowerCase()
      return lowerUrl.endsWith('.wav') || lowerUrl.includes('.wav?')
    },
    // 获取完整的URL：强制用配置的 WHITE_IP 重新拼接，
    // 即使后端返回完整 url(如 https://localhost:3333/...) 也提取 path 部分用 WHITE_IP 重写，
    // 确保 PC/H5 部署在任何环境都指向 https://wdfgdzx.top:3333/document/xxx.wav
    getFullUrl(url) {
      if (!url) return ''
      let path = url
      // 如果后端返回完整 url，提取其中的 path 部分
      if (url.startsWith('http')) {
        try {
          const u = new URL(url)
          path = u.pathname + (u.search || '')
        } catch (e) {
          // URL 解析失败，保留原值
          path = url
        }
      }
      return `https://${this.WHITE_IP}` + (path.startsWith('/') ? path : '/' + path)
    },
    // 在线播放音频（支持多次点击，点击同一行从头重播）
    async playInlineAudio(row) {
      if (!row || !row.url) {
        this.$message.warning('文件地址无效')
        return
      }
      // 先暂停其他正在播放的音频
      this.stopOtherAudios(row)
      const url = this.getFullUrl(row.url)
      const audio = this.getAudioByRow(row)
      if (!audio) {
        return
      }
      try {
        // 尝试使用Blob方式加载音频，避免服务端流式响应导致的播放异常（时长不准、重复播放）
        let blobUrl = this.audioCache[url]
        if (!blobUrl) {
          const response = await fetch(url)
          if (response.ok) {
            const blob = await response.blob()
            blobUrl = URL.createObjectURL(blob)
            this.audioCache[url] = blobUrl
          }
        }
        if (blobUrl && audio.src !== blobUrl) {
          audio.src = blobUrl
        }
      } catch (error) {
        console.warn('Blob加载音频失败，降级使用原在线地址播放:', error)
      }
      audio.currentTime = 0 // 从头播放
      await audio.play()
    },
    // 获取某行对应的audio元素
    getAudioByRow(row) {
      const refs = this.$refs['audio_' + row.id]
      return refs && refs.length ? refs[0] : null
    },
    // 音频开始播放（点击原生播放控件也会触发）
    onAudioPlay(row) {
      this.stopOtherAudios(row)
      this.currentPlayingId = row.id
    },
    // 暂停除当前行外的其他音频，保证同一时间只播放一个
    stopOtherAudios(row) {
      this.list.forEach(item => {
        if (item.id !== row.id) {
          const audio = this.getAudioByRow(item)
          if (audio && !audio.paused) {
            audio.pause()
            audio.currentTime = 0
          }
        }
      })
    },
    // 音频被暂停（点击原生控件的暂停键）
    onAudioPause(row) {
      if (this.currentPlayingId === row.id) {
        this.currentPlayingId = null
      }
    },
    // 音频播放结束
    onAudioEnded(row) {
      if (this.currentPlayingId === row.id) {
        this.currentPlayingId = null
      }
    },
    // 上传前校验
    beforeUpload(file) {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.type === 'application/vnd.ms-excel'
      if (!isExcel) {
        this.$message.error('只能上传 Excel 文件 (.xlsx, .xls)')
        return false
      }
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        this.$message.error('文件大小不能超过 10MB')
        return false
      }
      return true
    },
    onImportSuccess(response) {
      if (response && response.code === '200') {
        this.$message.success('文件上传成功')
        this.selectListPage()
      } else {
        this.$message.error(response?.message || '上传失败')
      }
    },
    reset() {
      this.send.name = ''
      this.send.currentPage = 1
      this.selectListPage()
    },
    onCurrentChange(currentPage) {
      this.send.currentPage = currentPage
      this.selectListPage()
    },
    onSizeChange(pageSize) {
      this.send.pageSize = pageSize
      this.send.currentPage = 1
      this.selectListPage()
    },
    selectListPage() {
      this.loading = true
      this.$http.post("/document/list_page", this.send).then(res => {
        if (res.data.code === "200") {
          this.send.total = res.data.object.total || 0
          this.list = (res.data.object.data || []).map(row => {
            // 从 originContent 末尾解析材长标记 ;WOODLEN=X（后端不改时，材长随文本一起存储）
            let displayContent = row.originContent || ''
            let parsedWoodLen = 6
            const m = /;WOODLEN=([\d.]+)\s*$/i.exec(displayContent)
            if (m) {
              parsedWoodLen = Number(m[1]) || 6
              displayContent = displayContent.substring(0, m.index).replace(/;+\s*$/, '')
            }
            this.$set(row, 'woodLen', row.woodLen != null ? Number(row.woodLen) : parsedWoodLen)
            this.$set(row, 'displayContent', displayContent)
            // 优先保留后端返回的历史统计记录；后端无 statText 时本地计算（用剥离标记后的纯文本）
            if (row.statText == null || row.statText === '') {
              this.$set(row, 'statText', calcStat(displayContent, row.woodLen))
            }
            return row
          })
          // 清除播放状态
          this.currentPlayingId = null
        } else {
          this.$message.error('查询失败，' + (res.data.message || res.data.code))
          if (res.data.code === '401') router.push("/login")
        }
      }).catch(err => {
        console.error('查询异常:', err)
        this.$message.error('请求异常，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },

    // 材长变更：实时重算该行统计（用剥离 WOODLEN 标记后的纯文本）
    onWoodLenChange(row) {
      row.statText = calcStat(row.displayContent, Number(row.woodLen))
    },

    // 径级分段统计算法已抽离到 @/js_util/statUtil 公共模块，与 AMeeting.vue 共用
    onSelectChange(val) {
      this.sendForm.removeIdList = val || []
    },
    // 批量下载
    async batchDownload() {
      if (!this.sendForm.removeIdList || this.sendForm.removeIdList.length === 0) {
        this.$message.warning('请先选择要下载的文件')
        return
      }

      const loadingMessage = this.$message({
        message: '正在打包下载文件，请稍候...',
        duration: 0,
        iconClass: 'el-icon-loading'
      });

      try {
        const zip = new JSZip();
        const downloadPromises = this.sendForm.removeIdList.map(async (file) => {
          try {
            const downloadUrl = this.getFullUrl(file.url);
            const response = await fetch(downloadUrl);
            if (!response.ok) {
              throw new Error(`下载失败: ${file.name}`);
            }
            const blob = await response.blob();
            // 生成文件名：原文件名_上传时间.扩展名
            const fileName = this.generateFileName(file);
            zip.file(fileName, blob, {binary: true});
          } catch (error) {
            console.error(`文件 ${file.name} 下载失败:`, error);
            throw new Error(`文件 ${file.name} 下载失败`);
          }
        });

        await Promise.all(downloadPromises);

        const zipBlob = await zip.generateAsync({type: "blob"});
        const zipFileName = `批量下载_${this.formatTimeForFileName(new Date())}.zip`;
        saveAs(zipBlob, zipFileName);

        loadingMessage.close();
        this.$message.success('批量下载成功');
      } catch (error) {
        loadingMessage.close();
        this.$message.error(error.message || '批量下载失败，请重试');
      }
    },
    confirmRemoveIdList() {
      if (!this.sendForm.removeIdList || this.sendForm.removeIdList.length === 0) {
        this.$message.warning('请先选择要删除的文件')
        return
      }
      const ids = this.sendForm.removeIdList.map(v => v.id)
      this.$http.post("/document/list_delete", {removeIdList: ids}).then(res => {
        if (res.data.code === "200") {
          this.$message.success('批量删除成功')
          this.sendForm.removeIdList = []
          this.selectListPage()
        } else {
          this.$message.error('批量删除失败，' + res.data.message)
        }
      }).catch(err => {
        this.$message.error('删除失败，请重试')
      })
    },
    confirmDelete(id) {
      this.$http.post("/document/delete", {id}).then(res => {
        if (res.data.code === "200") {
          this.$message.success('删除成功')
          this.selectListPage()
        } else {
          this.$message.error('删除失败，' + res.data.message)
        }
      }).catch(err => {
        this.$message.error('删除失败，请重试')
      })
    },
    changeEnable(row) {
      const originalStatus = row.enable
      this.$http.post("/document/insertOrUpdate", JSON.stringify(row)).then(res => {
        if (res.data.code === "200") {
          this.$message.success(row.enable ? '已启用' : '已禁用')
        } else {
          this.$message.error('操作失败，' + res.data.message)
          row.enable = originalStatus // 回滚状态
        }
      }).catch(() => {
        row.enable = originalStatus
        this.$message.error('请求失败，请重试')
      })
    },
    // 下载文件 - 使用uploadTime作为文件名
    async list_export(row) {
      if (!row.url) {
        this.$message.warning('文件地址无效')
        return
      }

      // 下载也用 getFullUrl 统一拼接（强制 WHITE_IP）
      let downloadUrl = this.getFullUrl(row.url)

      // 生成文件名：原文件名_上传时间.扩展名
      const fileName = this.generateFileName(row)

      try {
        // 使用fetch下载文件并重命名
        const response = await fetch(downloadUrl)
        const blob = await response.blob()

        // 创建下载链接
        const link = document.createElement('a')
        const objectUrl = URL.createObjectURL(blob)
        link.href = objectUrl
        link.download = fileName

        // 触发下载
        document.body.appendChild(link)
        link.click()

        // 清理
        document.body.removeChild(link)
        URL.revokeObjectURL(objectUrl)

        this.$message.success('下载成功')
      } catch (error) {
        console.error('下载失败:', error)
        // 如果fetch失败，降级使用window.open方式（但无法自定义文件名）
        this.$message.warning('使用备用方式下载，文件名可能为原文件名')
        window.open(downloadUrl, '_blank')
      }
    },
    // 生成文件名：原文件名_上传时间.扩展名
    generateFileName(row) {
      // 获取原文件名（不含扩展名）
      let originalName = row.name || 'document'
      const lastDotIndex = originalName.lastIndexOf('.')
      let nameWithoutExt = originalName
      let ext = ''

      if (lastDotIndex > 0) {
        nameWithoutExt = originalName.substring(0, lastDotIndex)
        ext = originalName.substring(lastDotIndex)
      }

      // 格式化上传时间
      let timeStr = ''
      if (row.uploadTime) {
        // 将上传时间转换为 YYYYMMDD_HHMMSS 格式
        timeStr = this.formatTimeForFileName(row.uploadTime)
      } else {
        // 如果没有上传时间，使用当前时间
        timeStr = this.formatTimeForFileName(new Date())
      }

      return `${timeStr}${ext}`
    },
    // 格式化时间为文件名可用格式
    formatTimeForFileName(time) {
      if (!time) return ''

      let date
      if (typeof time === 'string') {
        // 尝试解析字符串时间
        date = new Date(time)
        if (isNaN(date.getTime())) {
          // 如果解析失败，使用当前时间
          date = new Date()
        }
      } else if (time instanceof Date) {
        date = time
      } else {
        date = new Date()
      }

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')

      return `${year}${month}${day}_${hours}${minutes}${seconds}`
    }
  },
  beforeDestroy() {
    // 释放缓存的音频Blob URL，避免内存泄漏
    Object.keys(this.audioCache).forEach(key => {
      URL.revokeObjectURL(this.audioCache[key])
    })
    this.audioCache = {}
  }
}
</script>

<style scoped>
.document-container {
  padding: 20px;
  background-color: #f0f2f6;
  min-height: 100vh;
}

/* 操作栏 */
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  background: #fff;
  padding: 12px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.action-group {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.search-input {
  width: 240px;
}

/* 表格样式 */
.data-table {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  background-color: #fff;
}

.data-table >>> .el-table__header th {
  font-size: 14px;
  padding: 12px 0;
}

.data-table >>> .el-table__body td {
  font-size: 13px;
  padding: 8px 0;
}

/* 转写内容和统计列共用的预览样式 */
.content-preview-wrapper {
  width: 100%;
  cursor: help;
}

.content-preview {
  color: #606266;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  word-break: break-all;
}

/* 统计列特有的样式 */
.stat-cell {
  width: 100%;
}

.stat-wood-len {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.stat-wood-len .stat-label {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.stat-pre {
  margin: 0;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  line-height: 1.6;
  color: #303133;
  white-space: pre-wrap; /* 保留换行与空格 */
  word-break: break-all;
  background: #f5f7fa;
  border-radius: 4px;
  padding: 6px 8px;
}

.stat-empty {
  color: #909399;
  font-size: 12px;
}

/* 文件地址单元格样式 */
.file-address-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
}

.address-text {
  color: #606266;
  font-size: 12px;
  word-break: break-all;
  line-height: 1.4;
}

.play-inline-btn {
  color: #409eff;
  font-weight: normal;
  padding: 0;
  font-size: 12px;
}

.play-inline-btn:hover {
  color: #66b1ff;
  text-decoration: underline;
}

.play-inline-btn.is-playing {
  color: #67c23a;
}

.inline-audio-player {
  margin-top: 4px;
  width: 100%;
}

.inline-audio {
  width: 100%;
  height: 36px;
  outline: none;
  border-radius: 4px;
}

.empty-text {
  color: #c0c4cc;
  font-style: italic;
}

/* 分页区域 */
.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  background: #fff;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

/* 响应式 */
@media (max-width: 768px) {
  .action-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .action-group {
    justify-content: space-between;
  }

  .search-input {
    width: 100%;
  }
}
</style>
